import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Target, Sparkles, X, Briefcase } from "lucide-react";

const GOAL_KINDS = [
  { key: "job",           label: "Get a job",                emoji: "💼", placeholder: "land a junior data-analyst role in 3 months" },
  { key: "interview",     label: "Prepare for an interview", emoji: "🎯", placeholder: "become interview-ready for backend roles" },
  { key: "university",    label: "University / College",     emoji: "🎓", placeholder: "ace my databases coursework this semester" },
  { key: "work",          label: "Improve at work",          emoji: "🚀", placeholder: "automate repetitive spreadsheet work with Python" },
  { key: "personal",      label: "Personal development",     emoji: "🌱", placeholder: "learn one solid programming language this year" },
  { key: "research",      label: "Research",                 emoji: "🔬", placeholder: "prepare for a research project on ML pipelines" },
  { key: "business",      label: "Business",                 emoji: "📊", placeholder: "build dashboards for my startup's KPIs" },
  { key: "certification", label: "Certification",            emoji: "🏅", placeholder: "pass the AWS Solutions Architect exam" },
  { key: "other",         label: "Other",                    emoji: "✨", placeholder: "describe your goal in your own words" },
];

const INDUSTRIES = [
  "Engineering", "Software / IT", "Finance", "Healthcare", "Education",
  "Research", "Business Analytics", "Data Science", "Manufacturing",
  "Marketing", "Design", "Student", "Other",
];

/**
 * A gate that pops up an unskippable modal for users who don't yet have a
 * learning_goal. Also lets returning users update it. Renders nothing if user
 * already has a goal.
 */
export function LearningGoalGate() {
  const { user, refreshMe } = useAuth();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("interview");
  const [target, setTarget] = useState("");
  const [industry, setIndustry] = useState("");
  const [role, setRole] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && !user.learning_goal?.target) setOpen(true);
    else setOpen(false);
  }, [user]);

  const save = async () => {
    if (!target.trim() || target.trim().length < 3) {
      toast.error("Describe your goal in a sentence"); return;
    }
    if (!industry) { toast.error("Pick your industry"); return; }
    setBusy(true);
    try {
      await api.put("/me/learning-goal", { kind, target: target.trim() });
      await api.put("/me/profession", { industry, role: role.trim() || undefined });
      toast.success("Goal saved — Lane will tailor every lesson to it");
      await refreshMe();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!open || !user) return null;

  const selected = GOAL_KINDS.find(g => g.key === kind);

  return (
    <div data-testid="learning-goal-gate" className="fixed inset-0 z-50 grid place-items-center bg-black/80 backdrop-blur">
      <div className="relative w-[min(94vw,680px)] max-h-[92vh] overflow-y-auto rounded-3xl border border-primary/30 bg-card p-6 md:p-8 shadow-2xl">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-full border border-primary/40 bg-primary/10 text-primary">
            <Target className="h-4 w-4" />
          </div>
          <div>
            <div className="label-mono text-primary">// welcome to lanex</div>
            <div className="font-display text-xl font-black">One quick step first.</div>
          </div>
        </div>
        <p className="mt-4 text-sm text-muted-foreground">
          Every lesson, example, quiz and encouragement from Lane will be tailored to <em>your</em> goal and industry.
          Take 30 seconds — it makes everything downstream far more personal.
        </p>

        <div className="mt-6">
          <div className="label-mono mb-2">// your primary goal</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {GOAL_KINDS.map(g => (
              <button
                key={g.key}
                onClick={() => setKind(g.key)}
                data-testid={`goal-kind-${g.key}`}
                className={`text-left rounded-xl border p-3 transition ${kind === g.key ? "border-primary bg-primary/10" : "border-border hover:border-primary/40"}`}
              >
                <div className="text-lg">{g.emoji}</div>
                <div className="mt-1 text-xs font-medium">{g.label}</div>
              </button>
            ))}
          </div>

          <div className="mt-5">
            <label className="label-mono">// describe it in your own words</label>
            <Textarea
              data-testid="goal-target-input"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={selected?.placeholder}
              className="mt-2 min-h-[70px] rounded-lg"
              maxLength={200}
            />
          </div>
        </div>

        <div className="mt-6">
          <div className="label-mono mb-2 flex items-center gap-2"><Briefcase className="h-3.5 w-3.5 text-primary" /> // your industry / field</div>
          <div className="flex flex-wrap gap-2">
            {INDUSTRIES.map((ind) => (
              <button
                key={ind}
                onClick={() => setIndustry(ind)}
                data-testid={`industry-${ind.split(" / ")[0].toLowerCase()}`}
                className={`rounded-full border px-3 py-1 text-xs transition ${industry === ind ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/40 text-muted-foreground"}`}
              >
                {ind}
              </button>
            ))}
          </div>
          <div className="mt-3">
            <label className="label-mono">// your role (optional)</label>
            <Input
              data-testid="profession-role-input"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g. Backend Engineer, MSc student, Analyst"
              className="mt-2"
              maxLength={80}
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-3">
          <div className="label-mono">You can update this anytime in Settings.</div>
          <Button
            data-testid="goal-gate-save"
            onClick={save}
            disabled={busy || !target.trim() || !industry}
            className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest"
          >
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            {busy ? "Saving…" : "Save & start learning"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default LearningGoalGate;
